/**
 * One `BroadcastChannel('quadratic')` per tab (architecture §3.14, ADR-0039 decision 3): the app's own messages
 * (theme) and the sync client's (logout, session-renewed, …) share it, so a tab never hears itself and every message
 * carries the account id. The sync client gets this channel through its `Env` (apps/web/src/app/boot/client.ts).
 */
import type { Broadcast } from '@quadratic/sync-client';

type Message = { type: string; accountId: string; [k: string]: unknown };

class AppChannel implements Broadcast {
  private channel: BroadcastChannel | null = null;
  private readonly listeners = new Set<(m: Message) => void>();

  private open(): BroadcastChannel | null {
    if (this.channel) return this.channel;
    try {
      this.channel = new BroadcastChannel('quadratic');
      this.channel.onmessage = (ev: MessageEvent) => {
        const m = ev.data as Message | null;
        if (!m || typeof m !== 'object' || typeof m.type !== 'string') return;
        for (const fn of [...this.listeners]) fn(m);
      };
    } catch {
      this.channel = null;
    }
    return this.channel;
  }

  post(message: Message): void {
    try {
      this.open()?.postMessage(message);
    } catch {
      // a closed or unsupported channel: other tabs catch up through the storage event / next boot
    }
  }

  subscribe(fn: (message: Message) => void): () => void {
    this.open();
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

export const appChannel = new AppChannel();
